import type { AnyObject } from '../Types';
import type { WidgetInstance } from './dashboard.service';
import { utils } from '../Utils/mx-utils';
import type { HttpService } from '../App/services/http-service';
import { LocalizationStringDTO, TranslationService } from '../Shell/PandoraSolutionBuilderAgent/services/mx-translation-service';
import { LanguageGroupFilter } from '../Shell/PandoraSolutionBuilderAgent/model/enumerations';

export interface LocalizeField {
	localKey: string;
	fullKey: string;
	isArray: boolean;
	parentKey: string;
	applyCurrentValue: () => void;
}

export type TranslationRecord = Record<string, string>;

interface Translations {
	[lcid: number]: TranslationRecord;
}

export interface Localization {
	current: TranslationRecord;
	fields: LocalizeField[];
	translations?: Translations;
	fullLocalizationDTO?: LocalizationStringDTO[];
}

export class DashboardLocalizationService {
	static readonly $inject = ['mx.SolutionBuilderAgent.Http', 'mx.SolutionBuilderAgent.TranslationService'];

	constructor(
		private readonly http: HttpService,
		private readonly translationService: TranslationService
	) {}

	public async loadTranslations(widgetInstance: WidgetInstance) {
		if (!widgetInstance.dto.FullLocalization) {
			widgetInstance.dto.FullLocalization = widgetInstance.isNew ? [] : await this.http.get<LocalizationStringDTO[]>(`api/widgetInstances/${widgetInstance.id}/localization`);
		}

		if (widgetInstance.localization.translations) {
			return;
		}

		const fullLocalizationDTO: LocalizationStringDTO[] = JSON.parse(JSON.stringify(widgetInstance.dto.FullLocalization));
		widgetInstance.localization.fullLocalizationDTO = fullLocalizationDTO;

		const languages = await this.translationService.getAllLanguages(LanguageGroupFilter.UI);
		const translations: Translations = {};

		languages.forEach((language) => {
			const lcid = language.isBaseLanguageInGroup ? language.parentLcid : language.lcid;
			translations[lcid] = this.prepareTranslationProxy(lcid, fullLocalizationDTO);
		});

		fullLocalizationDTO.forEach((fullLocalizationValue) => {
			fullLocalizationValue.Translations.forEach((translationRecord) => {
				if (translations[translationRecord.Lcid]) {
					translations[translationRecord.Lcid][fullLocalizationValue.FrontendKey] = translationRecord.Value;
				}
			});
		});

		widgetInstance.localization.translations = translations;
	}

	private prepareTranslationProxy(lcid: number, localizationStringDTOs: LocalizationStringDTO[]) {
		const translations: TranslationRecord = {};

		const proxy = new Proxy<TranslationRecord>(translations, {
			get(target: TranslationRecord, frontendKey: string): string {
				return target[frontendKey];
			},
			set(target: TranslationRecord, frontendKey: string, newValue: string): boolean {
				if (!Object.hasOwn(target, frontendKey)) {
					let localizationStringDTO = localizationStringDTOs.find((record) => record.FrontendKey === frontendKey);
					if (!localizationStringDTO) {
						localizationStringDTO = {
							Value: newValue,
							FrontendKey: frontendKey,
							Key: utils.guid(),
							Translations: [],
							ObjectId: '',
						};
						localizationStringDTOs.push(localizationStringDTO);
					}
					let translationRecord = localizationStringDTO.Translations.find((record) => record.Lcid === lcid);
					if (!translationRecord) {
						translationRecord = {
							Lcid: lcid,
							Value: newValue,
						};
						localizationStringDTO.Translations.push(translationRecord);
					}

					Object.defineProperty(target, frontendKey, {
						get() {
							return translationRecord.Value;
						},
						set(newValue) {
							translationRecord.Value = newValue;
						},
					});
				}
				target[frontendKey] = newValue;
				return true;
			},
		});

		return proxy;
	}

	public localizeDataModel(dataModel: AnyObject, translations: TranslationRecord): LocalizeField[] {
		const fields = this.localizeValue(dataModel, '', translations);
		fields.forEach((field) => {
			field.parentKey = field.parentKey ? `vm.dataModel.WidgetConfiguration.${field.parentKey}` : '';
			field.fullKey = field.fullKey ? `vm.dataModel.WidgetConfiguration.${field.fullKey}` : '';
		});
		return fields;
	}

	public copyFullLocalizationDTO(fullLocalizationDTO: Localization['fullLocalizationDTO']): Localization['fullLocalizationDTO'] {
		if (!fullLocalizationDTO) {
			return;
		}
		const localizationStringDTOs: LocalizationStringDTO[] = JSON.parse(JSON.stringify(fullLocalizationDTO));
		localizationStringDTOs.forEach((localizationStringDTO) => {
			localizationStringDTO.Key = utils.guid();
			localizationStringDTO.ObjectId = '';
		});
		return localizationStringDTOs;
	}

	private applyTranslations(translations: TranslationRecord, parent: AnyObject, fields: LocalizeField[], isArray = false) {
		if (isArray && fields.length > 0) {
			parent.$localizationId = parent.$localizationId || utils.guid();
		}
		fields.forEach(({ localKey, fullKey }) => {
			fullKey = isArray ? fullKey.replace('[$localizationId]', `[${parent.$localizationId}]`) : fullKey;
			if (translations[fullKey]) {
				parent[localKey] = translations[fullKey];
			}
		});
	}

	private getLocalizedFields(value: AnyObject) {
		if (typeof value === 'object' && value !== null) {
			return Object.entries(value)
				.filter(([, childValue]) => typeof childValue === 'string')
				.map(([childKey]) => childKey);
		} else {
			return [];
		}
	}

	private getLocalizedChildren(value: AnyObject) {
		if (typeof value === 'object' && value !== null) {
			return Object.entries(value).filter(([, childValue]) => typeof childValue === 'object' && childValue !== null);
		} else {
			return [];
		}
	}

	private localizeValue(value: AnyObject | Array<unknown>, parentKey: string, translations: TranslationRecord): LocalizeField[] {
		const isArray = Array.isArray(value);
		const getFullKey = (field: string) => `${parentKey}${parentKey ? (isArray ? '[$localizationId].' : '.') : ''}${field}`;
		const isLocalizableChild = (child: unknown): child is Record<string, string> => typeof child === 'object' && child !== null;

		const localizedFields = this.getLocalizedFields(isArray ? value[0] : value).map((field) => {
			const fullKey = getFullKey(field);
			return {
				localKey: field,
				parentKey,
				fullKey,
				isArray,
				applyCurrentValue: () => {
					if (isArray) {
						value.forEach((child) => {
							if (isLocalizableChild(child)) {
								const key = fullKey.replace('[$localizationId]', `[${child.$localizationId}]`);
								if (translations[key]) {
									translations[key] = child[field];
								}
							}
						});
					} else {
						if (translations[fullKey]) {
							translations[fullKey] = value[field];
						}
					}
				},
			};
		});

		if (isArray) {
			value.forEach((child) => {
				if (typeof child === 'object' && child !== null) {
					this.applyTranslations(translations, child, localizedFields, isArray);
				}
			});
		} else {
			this.applyTranslations(translations, value, localizedFields);

			const children = this.getLocalizedChildren(value);
			children.forEach(([childKey, child]) => {
				const childFields = this.localizeValue(child, getFullKey(childKey), translations);
				localizedFields.push(...childFields);
			});
		}

		return localizedFields;
	}
}
